import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useEnrolUser } from '@justgiving/experiments';
import Loader from '@justgiving/loading/es/Loading';
import { useTracking } from '@justgiving/react-tracking';
import classNames from 'classnames';

import { PageStatus } from '../../../types/__generated__';
import { Experiments } from '../../constants/experiments';
import pageTypes from '../../constants/pageTypes';
import { useDonationAmount, usePageMeta } from '../../hooks';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import isLightColor from '../../utils/colors/isLightColor';
import isMobile from '../../utils/isMobile';

import styles from './DonateButton.scss';

type DonateButtonProps = {
  className?: string;
  inactivePageBehaviour?: 'disable' | 'hide';
  onClick?: () => void;
  trackingDataPageSection?: string;
  experiments?: string[];
  successType?: string;
  label?: string;
};

export const DonateButton: FunctionComponent<DonateButtonProps> = ({
  className,
  inactivePageBehaviour = 'disable',
  onClick,
  trackingDataPageSection,
  experiments,
  successType,
  label,
}) => {
  const basePageData = useBasePageQuery();
  const { currentUrl, pageType } = usePageMeta();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('common', { useSuspense: false });
  const track = useTracking();
  // AB test: no_tip_jar_on_amount_page
  // Can be removed when experiment has ended
  const { donationAmount, hideDonationAmount } = useDonationAmount();

  // B2C_045 - Donate button color
  // Remove this when removing this experiment
  const pageIsBranded = Boolean(
    basePageData.data?.page?.branding?.primaryColor,
  );
  const pageIsFundraising = pageType === pageTypes.FUNDRAISING;

  // B2C_045 - Donate button color
  // Remove this when removing this experiment
  const { enrolledIn: B2C_045_Enrolment } = useEnrolUser(
    Experiments.b2c_045_donate_button_color,
    !basePageData.data?.page || pageIsBranded || !pageIsFundraising,
  );

  if (!basePageData.data?.page) {
    return null;
  }

  const { status, relationships, branding, lifecycle } = basePageData.data.page;
  //  lifecycle is a PageStatus equivalent for PS pages
  //  it contains an array with allowed actions that should drive the front-end.
  const isDonateActive =
    status === PageStatus.Active ||
    (lifecycle?.allowedActions?.indexOf('public_donate') !== -1 &&
      lifecycle?.allowedActions?.indexOf('public_donate') !== undefined);

  const isMultiBeneficiaryCampaign =
    relationships && relationships.beneficiaries.totalCount > 1;

  if (inactivePageBehaviour === 'hide' && !isDonateActive) {
    return null;
  }

  const buttonType =
    branding?.primaryColor && isLightColor(branding.primaryColor)
      ? 'jg-btn--ghost'
      : 'jg-btn--themed';

  function handleClick() {
    setLoading(true);

    track({
      page_section: `${pageType} page > ${trackingDataPageSection ||
        'unknown'}`.toLowerCase(),
      event: 'link click',
      action_name: 'link click',
      event_value: 'donate',
      subtype: 'donate',
    });

    if (onClick) {
      onClick();
    }
  }

  const buildQueryString = () => {
    const urlParams = new URLSearchParams();

    if (donationAmount) {
      urlParams.append('amount', donationAmount.toString());
      urlParams.append('hideAmount', hideDonationAmount.toString());
      urlParams.append('isMobile', isMobile(window).toString());
    }

    const trimmedSuccessType = successType?.trim();
    const trimmedExperiments = experiments
      ?.map(e => e.trim())
      .filter(e => e !== '');

    if (
      trimmedExperiments &&
      trimmedExperiments.length > 0 &&
      trimmedSuccessType
    ) {
      urlParams.append('experiments', trimmedExperiments.join(','));
      urlParams.append('successType', trimmedSuccessType);
    }

    return urlParams.toString() ? `?${urlParams.toString()}` : '';
  };

  const donateUrl = `${currentUrl}/donate${buildQueryString()}`;

  return !isMultiBeneficiaryCampaign ? (
    <a
      type="button"
      className={classNames(
        'jg-btn',
        buttonType,
        styles.button,
        className,
        `jgp-b2c_045_${B2C_045_Enrolment}`,
        {
          [styles.disabled]: !isDonateActive,
        },
      )}
      onClick={handleClick}
      href={donateUrl}
      data-qa="donateButton"
    >
      {loading ? (
        <Loader size="small" colour="white" />
      ) : (
        label ?? t('common.donateButton')
      )}
    </a>
  ) : null;
};

export default DonateButton;
